import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, ClipboardPaste, CheckCircle2, XCircle, Copy, FileText, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const normalizeUrl = (url) => url.trim().replace(/\/+$/, '').toLowerCase();

const isValidUrl = (url) => {
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
};

// Falls back to a humanized domain/path when a clean label can't be derived.
const autoLabel = (url) => {
  try {
    const u = new URL(url);
    const segments = u.pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1] || '';
    const cleaned = last.replace(/[-_]+/g, ' ').replace(/\.\w+$/, '').trim();
    if (cleaned) {
      return cleaned.replace(/\b\w/g, (c) => c.toUpperCase());
    }
    return u.hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

// Splits free-form pasted text into individual URL candidates — handles
// one-per-line, comma-separated, and space-separated input. Every token is
// kept (even malformed ones) so invalid entries surface in the preview
// instead of silently disappearing.
const parsePastedText = (text) => {
  return text
    .split(/[\n,]+/)
    .flatMap((segment) => segment.split(/\s+/))
    .map((t) => t.trim())
    .filter(Boolean)
    .map((raw) => ({ label: '', url: raw }));
};

/**
 * BulkAddLinksModal — paste or upload many links at once, preview validity /
 * duplicates, then merge only the valid & non-duplicate ones into the task's
 * existing `links` array (same structure as individual add).
 */
const BulkAddLinksModal = ({ open, onOpenChange, existingLinks = [], onAdd, initialTab = 'paste' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [pasteText, setPasteText] = useState('');
  const [rows, setRows] = useState([]); // parsed, pre-validation
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // The modal stays mounted inside MultiLinkInput, so re-sync the active
  // tab every time it's (re)opened with a (possibly new) initialTab.
  React.useEffect(() => {
    if (open) setActiveTab(initialTab);
  }, [open, initialTab]);

  const resetState = () => {
    setPasteText('');
    setRows([]);
    setUploading(false);
  };

  const handleClose = (next) => {
    if (!next) resetState();
    onOpenChange(next);
  };

  const handlePasteChange = (e) => {
    const text = e.target.value;
    setPasteText(text);
    setRows(parsePastedText(text));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['txt', 'csv', 'xlsx', 'xls'].includes(ext)) {
      toast.error('Please upload a .txt, .csv, or .xlsx file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/tasks/parse-links-file`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setRows(res.data.rows || []);
      if (!res.data.rows?.length) {
        toast.error('No URLs found in this file');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not parse file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownloadSample = () => {
    const csvContent =
      'Label,URL\n' +
      'Homepage,https://example.com\n' +
      'SEO Guide,https://example.com/seo-guide\n' +
      'Google Drive,https://drive.google.com/example\n';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'links_sample_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Build the preview: valid / duplicate (vs existing links OR earlier row
  // in this same batch) / invalid — in one pass, in original order.
  const existingUrlSet = new Set((existingLinks || []).map((l) => normalizeUrl(l.url || '')));
  const seenInBatch = new Set();
  const preview = rows.map((r) => {
    const url = (r.url || '').trim();
    const label = (r.label || '').trim();
    let status = 'valid';
    if (!url || !isValidUrl(url)) {
      status = 'invalid';
    } else {
      const norm = normalizeUrl(url);
      if (existingUrlSet.has(norm) || seenInBatch.has(norm)) {
        status = 'duplicate';
      } else {
        seenInBatch.add(norm);
      }
    }
    return { label: label || (status === 'valid' ? autoLabel(url) : label), url, status };
  });

  const validCount = preview.filter((r) => r.status === 'valid').length;
  const duplicateCount = preview.filter((r) => r.status === 'duplicate').length;
  const invalidCount = preview.filter((r) => r.status === 'invalid').length;

  const handleAdd = () => {
    const toAdd = preview
      .filter((r) => r.status === 'valid')
      .map((r) => ({ label: r.label || 'Link', url: r.url }));
    if (!toAdd.length) return;
    onAdd(toAdd);
    toast.success(
      `${preview.length} links processed · ${toAdd.length} added` +
        (duplicateCount ? ` · ${duplicateCount} duplicate${duplicateCount > 1 ? 's' : ''} skipped` : '')
    );
    handleClose(false);
  };

  const statusBadge = (status) => {
    if (status === 'valid') {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
          <CheckCircle2 size={12} /> Valid
        </span>
      );
    }
    if (status === 'duplicate') {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
          <Copy size={12} /> Duplicate
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
        <XCircle size={12} /> Invalid
      </span>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl" data-testid="bulk-add-links-modal">
        <DialogHeader>
          <DialogTitle>Bulk Add Links</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="paste" data-testid="bulk-links-tab-paste">
              <ClipboardPaste size={14} className="mr-1.5" /> Paste Links
            </TabsTrigger>
            <TabsTrigger value="upload" data-testid="bulk-links-tab-upload">
              <Upload size={14} className="mr-1.5" /> Upload File
            </TabsTrigger>
          </TabsList>

          <TabsContent value="paste" className="space-y-2">
            <Textarea
              value={pasteText}
              onChange={handlePasteChange}
              placeholder={
                'Paste multiple URLs here — one URL per line\nExample:\nhttps://example.com/page-1\nhttps://example.com/page-2\nhttps://docs.google.com/document/...'
              }
              className="min-h-[140px] text-sm font-mono"
              data-testid="bulk-links-textarea"
            />
          </TabsContent>

          <TabsContent value="upload" className="space-y-3">
            <div
              className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-sky-300 hover:bg-sky-50/40 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              data-testid="bulk-links-upload-dropzone"
            >
              <FileText className="mx-auto mb-2 text-slate-400" size={28} />
              <p className="text-sm text-slate-600">
                {uploading ? 'Parsing file…' : 'Click to upload a .txt, .csv, or .xlsx file'}
              </p>
              <p className="text-xs text-slate-400 mt-1">CSV/XLSX may include Label + URL columns</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                data-testid="bulk-links-file-input"
              />
            </div>
            <button
              type="button"
              onClick={handleDownloadSample}
              className="text-xs font-medium text-sky-600 hover:text-sky-700 flex items-center gap-1"
              data-testid="bulk-links-download-sample-btn"
            >
              <Download size={12} /> Download Sample (Label,URL format)
            </button>
          </TabsContent>
        </Tabs>

        {rows.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm text-slate-600" data-testid="bulk-links-summary">
              {preview.length} links detected · {validCount} valid · {duplicateCount} duplicate
              {duplicateCount !== 1 ? 's' : ''} · {invalidCount} invalid
            </div>
            <div className="max-h-[240px] overflow-y-auto border border-slate-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-slate-500 w-8">#</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-500">Label</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-500">URL</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-500 w-24">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`bulk-links-row-${i}`}>
                      <td className="px-3 py-1.5 text-slate-400">{i + 1}</td>
                      <td className="px-3 py-1.5 text-slate-700 whitespace-nowrap">{r.label || '—'}</td>
                      <td className="px-3 py-1.5 text-slate-500 truncate max-w-[280px]" title={r.url}>
                        {r.url}
                      </td>
                      <td className="px-3 py-1.5">{statusBadge(r.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter className="static mt-2">
          <Button variant="outline" onClick={() => handleClose(false)} data-testid="bulk-links-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={validCount === 0} data-testid="bulk-links-confirm-btn">
            Add {validCount} Link{validCount !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkAddLinksModal;
